import React from 'react';
import styled from 'styled-components';
import SharedPageLayout from './SharedPageLayout';
import { theme } from '../theme';
import { getImageUrl } from '../utils/imageUtils';

const ContentSection = styled.div`
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 60px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 40px 12px;
  }
`;

const ParagraphContainer = styled.div`
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 30px;
  }
`;

const ParagraphTitle = styled.h2`
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 28px;
  line-height: 36px;
  color: ${theme.colors.outchou_green};
  margin: 50px 0 20px 0;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
    line-height: 28px;
    margin-bottom: 12px;
  }
`;

const ParagraphText = styled.div`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: ${theme.colors.outchou_blue};
  margin: 0;
  text-align: left;
  
  a {
    color: ${theme.colors.outchou_blue};
    text-decoration: none;
    font-weight: 700;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${theme.colors.outchou_green};
    }
  }
  
  ul {
    margin: 15px 0;
    padding-left: 0;
    list-style: none;
  }
  
  li {
    position: relative;
    padding-left: 24px;
    margin-bottom: 0px;
    
    &:before {
      content: "•";
      position: absolute;
      left: 0;
      top: 0;
      color: ${theme.colors.outchou_yellow};
      font-weight: bold;
      font-size: 18px;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 24px;
    
    ul {
      margin: 16px 0;
    }
    
    li {
      padding-left: 20px;
      margin-bottom: 5px;
      
      &:before {
        font-size: 16px;
      }
    }
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 22px;
    
    ul {
      margin: 12px 0;
    }
    
    li {
      padding-left: 18px;
      margin-bottom: 4px;
      
      &:before {
        font-size: 14px;
      }
    }
  }
`;

const CeramicImage = styled.div`
  margin: 30px 0;
  text-align: center;
  
  img {
    max-width: 300px;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
  }
  
  p {
    margin-top: 15px;
    font-family: ${theme.fonts.body};
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.outchou_blue};
    font-style: italic;
    text-align: center;
  }
  
  @media (max-width: 768px) {
    margin: 25px 0;
    
    img {
      max-width: 250px;
    }
    
    p {
      font-size: 13px;
      line-height: 18px;
    }
  }
  
  @media (max-width: 480px) {
    margin: 20px 0;
    
    img {
      max-width: 200px;
    }
    
    p {
      font-size: 12px;
      line-height: 16px;
    }
  }
`;

const AboutPage: React.FC = () => {
  return (
    <SharedPageLayout 
              backgroundImage={getImageUrl("/shared_assets/images/navigation/about.jpeg")}
      heroType="contact"
      heroTitle="L’Algérie autrement"
      heroSubtitle="Explorez les traditions, les arts et l’histoire de l’Algérie."
    >
      <ContentSection>
        <ParagraphContainer>
          <ParagraphTitle>Notre Mission</ParagraphTitle>
            <ParagraphText>
            Rendre la culture algérienne accessible à tous :
            ses danses, ses métiers d’art, ses cuisines régionales, ses vêtements, ses musiques, ses histoires… et surtout, les peuples qui forment cette mosaïque unique.
            <ul>Outchou veut être un espace où chacun peut explorer, comprendre et transmettre ce qui fait l’Algérie.</ul> 
            </ParagraphText>
          </ParagraphContainer>
      

        <ParagraphContainer>
          <ParagraphTitle>Pourquoi Outchou ?</ParagraphTitle>
          <ParagraphText>
          Le mot “Outchou” signifie <b>“couscous”</b> en Chleuhs et en Mozabites. 
          Un plat partagé par tous les Algériens, du nord au sud.
          <ul>Nous avons choisi ce nom pour ce qu’il représente : la diversité qui rassemble.</ul>
          </ParagraphText>
        </ParagraphContainer>
        
             
     

        <ParagraphContainer>
          <ParagraphTitle>Notre identité visuelle </ParagraphTitle>
          <ParagraphText>
          Nos couleurs s’inspirent du zellige des villes du Nord : <ul>
            <li><b>Bleu</b> : pour la mer Méditerranée .</li>
            <li><b>Vert turquoise</b>: pour la nature.</li>
            <li><b>Blanc</b>: pour la lumière.</li>
            <li><b>Jaune</b>: pour le soleil et le Sahara.</li>
          </ul>
          <CeramicImage>
            <img src={getImageUrl("/shared_assets/images/ceramique.jpeg")} alt="Pièce de céramique traditionnelle algérienne" />
            <p>Pièce de céramique typique visible au musée du Bardo à Alger.</p>
          </CeramicImage>
          Elles sont douces, fraîches, naturelles — et surtout, elles représentent une Algérie plurielle.
          </ParagraphText>
          
          <CeramicImage>
            <img src={getImageUrl("/shared_assets/images/fibule.jpeg")} alt="Fibule traditionnelle du Touat" />
            <p>Fibule traditionnelle du Touat, datant du début du XXe siècle, provenant de la ville d'Adrar et connue sous le nom de Kholalat es-Selk.</p>
          </CeramicImage>
          <ParagraphText>
          Nos formes, elles, viennent du <b>Sahara</b> : motifs inspirés des fibules traditionnelles du Touat, symboles ancestraux porteurs d’histoire et d’identité. C’est notre manière d’honorer le Sud.        
          </ParagraphText>
        </ParagraphContainer>

        <ParagraphContainer>
          <ParagraphTitle>Le chiffre 8 
          </ParagraphTitle>
          <ParagraphText>
          Elle vient de l’art islamique : une forme de perfection, d’harmonie et de spiritualité. 
          <ul>Elle rappelle l’identité musulmane de l’Algérie et son héritage architectural.</ul></ParagraphText>
        </ParagraphContainer>

        <ParagraphContainer>
          <ParagraphTitle>Pourquoi préserver notre culture ?
          </ParagraphTitle>
          <ParagraphText>
          Parce qu’une culture vivante unit, inspire et raconte qui nous sommes.
          </ParagraphText>
        </ParagraphContainer>
        <ParagraphContainer>
          <ParagraphText>
          Outchou existe pour protéger, célébrer et partager cette héritage avec tous.
          </ParagraphText>
        </ParagraphContainer>
        
      </ContentSection>
    </SharedPageLayout>
  );
};

export default AboutPage;